import _ from 'lodash'
import type { BaseDataItem, UnresolvedVariablesList, UnresolvedVariablesVar } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import type { Unref } from '@wix/thunderbolt-catharsis'
import { removeHash } from '@wix/thunderbolt-catharsis'
import type { Ref } from '@wix/thunderbolt-types'
import { getContextFromInflatedId } from '../../../byRefUtils'
import {
	PRESET_RESOLVING_DATA_ITEM_TYPE,
	QUERY_MAPPING,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	VARIABLES_INFLATED_MAP,
} from '../../../constants'
import { resolveOverrideItem } from '../../dataConsumption'
import { getInflatedDataItemsMap } from '../../presetsResolving'
import type { DataItemWithDependencies, DataRefsForPresetResolving } from '../../types'
import type { DependenciesData } from './types'
import { createConsumptionDataNode, getDataRefsForPresetResolving } from './utils/dataNodeUtils'

type Dependencies = {
	variablesListDataRefs?: Ref<DataItemWithDependencies>
	variablesDataRefs: Record<string, Ref<DataItemWithDependencies>>
	variablesDataRefsForPresetResolving: Record<string, DataRefsForPresetResolving>
}

export const Variables = createConsumptionDataNode<
	DependenciesData,
	Dependencies,
	DataRefsForPresetResolving | DataItemWithDependencies
>({
	getDependencies: (data) => {
		const { rawModel, queryName, widgetNamespace, templateComponent, createRefInTemplateNamespace } = data
		const dataRefs = { variablesDataRefs: {}, variablesDataRefsForPresetResolving: {} } as Dependencies

		const variablesMap = _.get(rawModel, [widgetNamespace, 'variables'])
		const query = templateComponent[queryName]

		// variableList will be defined only on the template rootCompId
		if (query && variablesMap[query]) {
			return _.reduce(
				variablesMap[query].variables,
				(acc, variableId) => {
					const variable = variablesMap[removeHash(variableId)] as UnresolvedVariablesVar
					acc.variablesDataRefs[variable.id] = createRefInTemplateNamespace(
						QUERY_MAPPING[queryName],
						variable.id
					)

					const refArrayId = removeHash(variable.value)
					acc.variablesDataRefsForPresetResolving[refArrayId] = getDataRefsForPresetResolving(
						data,
						refArrayId
					)

					return acc
				},
				{
					...dataRefs,
					variablesListDataRefs: createRefInTemplateNamespace(QUERY_MAPPING[queryName], query),
				}
			)
		}

		return dataRefs
	},
	updateDependencies: ({ update, inflatedComponent, dependencies }) => {
		const { variablesListDataRefs, variablesDataRefs, variablesDataRefsForPresetResolving } = dependencies

		if (variablesListDataRefs) {
			update(
				RESOLVED_REFS_NAMESPACE,
				'variables',
				inflatedComponent.variablesQuery,
				variablesListDataRefs,
				variablesListDataRefs
			)
		}

		for (const variableId in variablesDataRefs) {
			update(
				RESOLVED_REFS_NAMESPACE,
				'variables',
				`${inflatedComponent.id}-${variableId}`,
				variablesDataRefs[variableId],
				variablesDataRefs[variableId]
			)
		}

		for (const refArrayId in variablesDataRefsForPresetResolving) {
			update(
				RESOLVED_REFS_NAMESPACE,
				VARIABLES_INFLATED_MAP,
				`${inflatedComponent.id}-${refArrayId}`,
				variablesDataRefsForPresetResolving[refArrayId],
				variablesDataRefsForPresetResolving[refArrayId]
			)
		}
	},
	toViewItem: ({ id, aspect, value, rawModel }) => {
		if (aspect === VARIABLES_INFLATED_MAP) {
			const { refArraysWithOverrides } = value as Unref<DataRefsForPresetResolving>
			const templateDataItem = _.last(refArraysWithOverrides)
			if (!templateDataItem) {
				return
			}

			if (templateDataItem.type !== 'RefArray') {
				return { [id]: resolveOverrideItem(refArraysWithOverrides, id) }
			}

			const pageVariants = _.get(rawModel, [SITE_NAMESPACE, pointers.data.DATA_MAPS.VARIANTS], {})
			return getInflatedDataItemsMap(
				value as Unref<DataRefsForPresetResolving>,
				id,
				pageVariants,
				PRESET_RESOLVING_DATA_ITEM_TYPE[pointers.data.DATA_MAPS.VARIABLES]
			)
		} else {
			if (!value) {
				return
			}

			const type = (value as BaseDataItem).type

			const context = getContextFromInflatedId(id)
			const inflatedWidgetRootCompId = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, context])

			if (type === 'VariablesList') {
				const variables = (value as UnresolvedVariablesList).variables
				const inflatedVariables = _.map(
					variables,
					(variableId) => `#${inflatedWidgetRootCompId}-${removeHash(variableId)}`
				)

				return {
					...value,
					id,
					variables: inflatedVariables,
				}
			} else {
				const refArrayId = (value as UnresolvedVariablesVar).value

				return {
					...value,
					id,
					value: `#${inflatedWidgetRootCompId}-${removeHash(refArrayId)}`,
				}
			}
		}
	},
})
